import { useTranslation } from "react-i18next";
import { CapacityAlert, getMaxCapacity } from "userful-chronos-app-common-js/dist/models/notifications/notifications";
import UserfulTooltip from "../../Widgets/UserfulTooltip";

const getBackgroundStyle = (currentCapacity: CapacityAlert): string => {
    if (currentCapacity.usageLevel === "USAGE_LEVEL_1") {
        return `conic-gradient(rgba(35, 168, 102, 1) ${getMaxCapacity(currentCapacity) * 3.6}deg, rgba(35, 168, 102, 0.15) 0deg)`;
    }
    if (currentCapacity.usageLevel === "USAGE_LEVEL_2") {
        return `conic-gradient(rgba(255, 162, 0, 1) ${getMaxCapacity(currentCapacity) * 3.6}deg, rgba(255, 162, 0, 0.15) 0deg)`;
    }
    return `conic-gradient(rgba(209, 62, 44, 1) ${getMaxCapacity(currentCapacity) * 3.6}deg, rgba(209, 62, 44, 0.15) 0deg)`;
};

interface IProps {
    currentCapacity: CapacityAlert;
}

const CapacityPopup = ({ currentCapacity }: IProps) => {
    const { t } = useTranslation();
    const getWarningClass = (): string => {
        if (currentCapacity.usageLevel === "USAGE_LEVEL_1") {
            return "normal";
        }
        if (currentCapacity.usageLevel === "USAGE_LEVEL_2") {
            return "warn";
        }
        return "error";
    };

    return (
        <div className="usageDescription">
            <div className="usageTitle">{t("MonitoringApp.SystemInfo.usageTitle")}</div>
            <div className={`usageSummary ${getWarningClass()}`}>
                <UserfulTooltip
                    placement="bottom"
                    title={
                        <div className="usageBreakdown">
                            <p>
                                {t("MonitoringApp.SystemInfo.cpuUsage")}
                                {Math.round(currentCapacity.usagePct.cpuUsage)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.gpuUsage")}
                                {Math.round(currentCapacity.usagePct.gpuUtilization)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.gpuMemory")}
                                {Math.round(currentCapacity.usagePct.gpuMemoryUsage)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.encoderUsage")}
                                {Math.round(currentCapacity.usagePct.gpuEncoderUsage)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.decoderUsage")}
                                {Math.round(currentCapacity.usagePct.gpuDecoderUsage)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.memoryUsage")}
                                {Math.round(currentCapacity.usagePct.memoryUsage)}%
                            </p>
                            <p>
                                {t("MonitoringApp.SystemInfo.storageUsage")}
                                {Math.round(currentCapacity.usagePct.storageUsage)}%
                            </p>
                        </div>
                    }
                >
                    <div
                        className="circular-progress"
                        style={{
                            background: getBackgroundStyle(currentCapacity),
                        }}
                    >
                        <div className="inner-circle"></div>
                        <p className="percentage">{Math.round(getMaxCapacity(currentCapacity))}%</p>
                    </div>
                </UserfulTooltip>
                <div className="usageLevelText">{t(`MonitoringApp.SystemInfo.${currentCapacity.usageLevel}`)}</div>
            </div>
            <div className="usageInstruction">
                {(currentCapacity.usageLevel === "USAGE_LEVEL_1" || currentCapacity.usageLevel === "USAGE_LEVEL_2") && (
                    <>
                        <p>{t("MonitoringApp.SystemInfo.usageLowInstruction")}</p>
                        <ul>
                            <li>{t("MonitoringApp.SystemInfo.usageLowInstruction1")}</li>
                            <li>{t("MonitoringApp.SystemInfo.usageLowInstruction2")}</li>
                            <li>{t("MonitoringApp.SystemInfo.usageLowInstruction3")}</li>
                        </ul>
                    </>
                )}
                {(currentCapacity.usageLevel === "USAGE_LEVEL_3" || currentCapacity.usageLevel === "USAGE_LEVEL_4") && (
                    <>
                        <p>{t("MonitoringApp.SystemInfo.usageHighInstruction")}</p>
                        <ul>
                            <li>{t("MonitoringApp.SystemInfo.usageHighInstruction1")}</li>
                            <li>{t("MonitoringApp.SystemInfo.usageHighInstruction2")}</li>
                            <li>{t("MonitoringApp.SystemInfo.usageHighInstruction3")}</li>
                        </ul>
                    </>
                )}
            </div>
            {(currentCapacity.usageLevel === "USAGE_LEVEL_3" || currentCapacity.usageLevel === "USAGE_LEVEL_4") && (
                <p>{t("MonitoringApp.SystemInfo.usageHighWarning")}</p>
            )}
            <div>
                {t("MonitoringApp.SystemInfo.usageLicenseInstruction")}
                <a href="mailto:capacityalert@userful.com">capacityalert@userful.com</a>
            </div>
        </div>
    );
};

export default CapacityPopup;
